import { arrayHasItems, isArray, isPlainObject, merge, object, objectHasKeys, present } from '@art-suite/art-core-ts';
import { normalizeScanOrScanTemplatePartPropValues } from '../commonLib';
import { getAllParseAssetsForExpressions } from '../OptimizedValueExpressions/InternalLib';
import { isExpressionValue, ScanTemplatePart, ScanTemplateValue, ScanTemplateValues, ScanValueUpdates } from './ScanTemplateSchemas';

export const pushIfNotPresent = <T>(array: T[], item: T): void => {
  if (!array.includes(item)) {
    array.push(item);
  }
};

/**
 * A part's template value keys in dependency order: values that are not expressions come first, in
 * template order, then each expression after every value it depends on (the expressions' evaluation
 * order). An expression that fails to parse has no place in that order, so it lists last.
 */
export const getScanTemplateValueKeysInDependencyOrder = (values: ScanTemplateValues | undefined): string[] => {
  if (!values) return [];
  const expressions: Record<string, string> = {};
  const independentKeys: string[] = [];
  for (const key in values) {
    const templateValue = values[key];
    if (isExpressionValue(templateValue)) expressions[key] = templateValue.value;
    else independentKeys.push(key);
  }
  const orderedKeys = [...independentKeys, ...getAllParseAssetsForExpressions(expressions).evaluationOrder];
  return [...orderedKeys, ...Object.keys(expressions).filter(key => !orderedKeys.includes(key))];
};

// strip all optional values that are the default value (or null or undefined or [])
const normalizeTemplateValue = (templateValue: ScanTemplateValue): ScanTemplateValue => {
  const { category, description, type, value, required, valueOptions, ...rest } = templateValue;
  const newValue: ScanTemplateValue = { ...rest }; // using rest in case we add new fields to ScanTemplateValue and forget to update this function

  if (present(category)) newValue.category = category;
  if (present(description)) newValue.description = description;
  if (present(type)) newValue.type = type;
  if (present(value)) newValue.value = value;
  if (required) newValue.required = true;
  if (arrayHasItems(valueOptions)) newValue.valueOptions = valueOptions;

  return newValue;
};

const normalizeTemplateValues = (values: ScanTemplateValues): ScanTemplateValues =>
  object(values, { with: (value: ScanTemplateValue) => normalizeTemplateValue(value), when: value => value !== null });

const mergeTemplateValues = (current: ScanTemplateValues | undefined, updates: ScanValueUpdates | undefined | null): ScanTemplateValues | undefined =>
  current && updates
    ? normalizeTemplateValues(merge(current, updates))
    : updates
    ? normalizeTemplateValues(updates)
    : current;

export const dropEmptyObjects = <T>(obj: T | undefined): T | undefined => isPlainObject(obj) && objectHasKeys(obj) ? obj : undefined;
export const dropEmptyArrays = <T>(obj: T[] | undefined): T[] | undefined => isArray(obj) && arrayHasItems(obj) ? obj : undefined;

export const updateTemplateItemHelper = <T extends ScanTemplatePart>(
  current: T,
  updates: Partial<Omit<T, 'values'>> & ScanValueUpdates,
  now: number = Date.now(),
): T => {
  const newValues = 'values' in updates
    ? dropEmptyObjects(mergeTemplateValues(current.values, updates.values))
    : current.values;

  const result: T = {
    ...current,
    ...updates,
    updatedAt: now,
  } as T;

  if (newValues !== undefined) {
    result.values = newValues;
  } else {
    // at this point even though we have determined that the values property should be gone
    // a result object will still have the old values property on it (copied over from current)
    // this delete statement is our final, explicit step to clean up and ensure the result object
    // is in the correct final state
    delete (result as any).values;
  }

  return normalizeScanOrScanTemplatePartPropValues(result) as T;
};
